import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Dialog, DialogContent, DialogTrigger } from "../components/ui/dialog";
import { supabase } from "../lib/supabase";

const isVideoFile = (name) => /\.(mp4|webm|mov|m4v)$/i.test(name);

// ── Configuration des galeries ────────────────────────────────────
// Ajoute une entrée ici pour créer une nouvelle section (ex: "produits",
// "equipe", "evenements", etc.). Le rendu et le SEO s'adaptent automatiquement.
const GALLERY_SECTIONS = [
	{
		id: "ambiance",
		folder: "gallery",
		title: "L'ambiance du coffee shop",
		description: "Notre cocon chaleureux au cœur de Genève.",
		alt: "Ambiance Léger's Coffee",
		limit: 200,
	},
	{
		id: "afterworks",
		folder: "afterworks",
		title: "Nos afterworks",
		description:
			"Les meilleurs moments de nos soirées et événements Léger's Coffee.",
		alt: "Afterwork Léger's Coffee",
		limit: 200,
	},
	{
		id: "exterieurs",
		folder: "exterieurs",
		title: "Nos extérieurs",
		description:
			"Terrasse ensoleillée et façade du coffee shop, à deux pas du centre de Genève.",
		alt: "Extérieur Léger's Coffee",
		limit: 200,
	},
	{
		id: "sandwich",
		folder: "sandwich",
		title: "Nos sandwiches",
		description:
			"Pain artisanal, ingrédients frais et recettes signature — préparés chaque matin.",
		alt: "Sandwich Léger's Coffee",
		limit: 200,
	},
	{
		id: "evenements",
		folder: "evenements",
		title: "Nos événements",
		description:
			"Moments conviviaux et rencontres autour de nos cafés de spécialité.",
		alt: "Événements Léger's Coffee",
		limit: 200,
	},
];

function AnimatedGalleryItem({ children, index }) {
	const ref = useRef(null);
	const [isVisible, setIsVisible] = useState(false);

	useEffect(() => {
		const element = ref.current;

		if (!element) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.unobserve(element);
				}
			},
			{
				threshold: 0.15,
				rootMargin: "0px 0px -60px 0px",
			},
		);

		observer.observe(element);

		return () => observer.disconnect();
	}, []);

	return (
		<div
			ref={ref}
			className={`
				transition-all duration-700 ease-out
				${
					isVisible
						? "opacity-100 translate-y-0 scale-100"
						: "opacity-0 translate-y-8 scale-[0.98]"
				}
			`}
			style={{
				transitionDelay: isVisible ? `${(index % 6) * 100}ms` : "0ms",
			}}
		>
			{children}
		</div>
	);
}

export default function GalleryPage() {
	const [sections, setSections] = useState(
		GALLERY_SECTIONS.map((s) => ({ ...s, images: [], loading: true })),
	);

	useEffect(() => {
		let cancelled = false;

		const fetchAll = async () => {
			const results = await Promise.all(
				GALLERY_SECTIONS.map(async (section) => {
					const { data, error } = await supabase.storage
						.from("images")
						.list(section.folder, {
							limit: section.limit ?? 100,
							sortBy: { column: "name", order: "asc" },
						});

					if (error) {
						console.error(`Erreur chargement galerie ${section.id} :`, error);
						return { ...section, images: [], loading: false };
					}

					const images = (data ?? [])
						.filter((f) => f.name && !f.name.startsWith("."))
						.map((file) => {
							const { data: urlData } = supabase.storage
								.from("images")
								.getPublicUrl(`${section.folder}/${file.name}`);
							return {
								id: `${section.id}-${file.id ?? file.name}`,
								src: urlData.publicUrl,
								alt: section.alt,
								type: isVideoFile(file.name) ? "video" : "image",
							};
						});

					return { ...section, images, loading: false };
				}),
			);

			if (!cancelled) setSections(results);
		};

		fetchAll();

		return () => {
			cancelled = true;
		};
	}, []);

	// SEO — combine toutes les images de toutes les sections
	const allImages = useMemo(
		() => sections.flatMap((s) => s.images),
		[sections],
	);

	const pageTitle = "Galerie | Léger's Coffee Genève";
	const pageDescription =
		"Découvrez la galerie photo de Léger's Coffee à Genève : ambiance du coffee shop, afterworks, cafés de spécialité, pâtisseries artisanales et moments gourmands.";
	const canonicalUrl = "https://legers-coffee.com/galerie";
	const ogImage = allImages[0]?.src || "https://legers-coffee.com/og-image.jpg";

	const structuredDataCollectionPage = {
		"@context": "https://schema.org",
		"@type": "CollectionPage",
		name: "Galerie | Léger's Coffee",
		url: canonicalUrl,
		description: pageDescription,
		inLanguage: "fr-CH",
	};

	const structuredDataImageGallery = useMemo(
		() => ({
			"@context": "https://schema.org",
			"@type": "ImageGallery",
			name: "Galerie photos Léger's Coffee",
			url: canonicalUrl,
			description: pageDescription,
			associatedMedia: allImages.map((image) => ({
				"@type": "ImageObject",
				contentUrl: image.src,
				description: image.alt,
			})),
		}),
		[allImages],
	);

	const anyLoading = sections.some((s) => s.loading);
	const allEmpty = !anyLoading && sections.every((s) => s.images.length === 0);

	return (
		<>
			<Helmet>
				<title>{pageTitle}</title>
				<meta name="description" content={pageDescription} />
				<meta name="robots" content="index, follow" />
				<link rel="canonical" href={canonicalUrl} />

				<meta property="og:type" content="website" />
				<meta property="og:site_name" content="Léger's Coffee" />
				<meta property="og:locale" content="fr_CH" />
				<meta property="og:title" content={pageTitle} />
				<meta property="og:description" content={pageDescription} />
				<meta property="og:url" content={canonicalUrl} />
				<meta property="og:image" content={ogImage} />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={pageTitle} />
				<meta name="twitter:description" content={pageDescription} />
				<meta name="twitter:image" content={ogImage} />

				<script type="application/ld+json">
					{JSON.stringify(structuredDataCollectionPage)}
				</script>
				<script type="application/ld+json">
					{JSON.stringify(structuredDataImageGallery)}
				</script>
			</Helmet>

			<main data-testid="gallery-page" className="min-h-screen pt-20">
				{/* Hero */}
				<section className="bg-warm-secondary py-16 lg:py-24">
					<div className="max-w-7xl mx-auto px-6 lg:px-12">
						<div className="text-center max-w-2xl mx-auto">
							<span className="text-latte-gold text-sm uppercase tracking-widest font-medium mb-4 block">
								Notre Univers
							</span>
							<h1 className="font-serif text-4xl lg:text-5xl text-espresso font-semibold mb-6">
								Galerie
							</h1>
							<p className="text-soft-charcoal-secondary">
								Découvrez l'atmosphère chaleureuse de Léger's Coffee à travers
								notre galerie photos.
							</p>
						</div>
					</div>
				</section>

				{/* Une section par galerie */}
				{anyLoading && allImages.length === 0 ? (
					<section className="py-16 lg:py-24 bg-warm-alabaster">
						<div className="max-w-7xl mx-auto px-6 lg:px-12">
							<p className="text-center text-soft-charcoal-secondary py-12">
								Chargement de la galerie…
							</p>
						</div>
					</section>
				) : allEmpty ? (
					<section className="py-16 lg:py-24 bg-warm-alabaster">
						<div className="max-w-7xl mx-auto px-6 lg:px-12">
							<p className="text-center text-soft-charcoal-secondary py-12">
								Aucune image disponible pour le moment.
							</p>
						</div>
					</section>
				) : (
					sections.map((section, sectionIndex) => {
						// N'affiche pas une section vide
						if (!section.loading && section.images.length === 0) return null;

						// Alterne les fonds
						const bgClass =
							sectionIndex % 2 === 0
								? "bg-warm-alabaster"
								: "bg-warm-secondary";

						return (
							<section
								key={section.id}
								data-testid={`gallery-section-${section.id}`}
								className={`py-16 lg:py-24 ${bgClass}`}
							>
								<div className="max-w-7xl mx-auto px-6 lg:px-12">
									{/* En-tête de section */}
									<div className="mb-10 text-center max-w-2xl mx-auto">
										<h2 className="font-serif text-2xl lg:text-3xl text-espresso font-semibold mb-3">
											{section.title}
										</h2>
										{section.description && (
											<p className="text-soft-charcoal-secondary">
												{section.description}
											</p>
										)}
									</div>

									{/* Grille */}
									{section.loading ? (
										<p className="text-center text-soft-charcoal-secondary py-8">
											Chargement…
										</p>
									) : (
										<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
											{section.images.map((image, index) => (
												<AnimatedGalleryItem key={image.id} index={index}>
													<Dialog>
														<DialogTrigger asChild>
															<button
																type="button"
																className="group relative aspect-square overflow-hidden bg-warm-secondary cursor-pointer"
																data-testid={`gallery-image-${image.id}`}
															>
																{image.type === "video" ? (
																	<video
																		src={image.src}
																		muted
																		loop
																		playsInline
																		autoPlay
																		className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
																	/>
																) : (
																	<img
																		src={image.src}
																		alt={image.alt}
																		loading="lazy"
																		className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
																	/>
																)}

																<div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/20 transition-colors duration-300" />

																<div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-espresso/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
																	<p className="text-warm-alabaster text-sm font-medium">
																		{image.alt}
																	</p>
																</div>
															</button>
														</DialogTrigger>

														<DialogContent className="max-w-4xl p-0 bg-transparent border-none">
															{image.type === "video" ? (
																<video
																	src={image.src}
																	controls
																	autoPlay
																	className="w-full h-auto max-h-[80vh]"
																/>
															) : (
																<img
																	src={image.src}
																	alt={image.alt}
																	className="w-full h-auto max-h-[80vh] object-contain"
																/>
															)}
														</DialogContent>
													</Dialog>
												</AnimatedGalleryItem>
											))}
										</div>
									)}
								</div>
							</section>
						);
					})
				)}
			</main>
		</>
	);
}
